import React from 'react';

import Badge from '@/components/Badge';
import TableRowButtons from '@/components/Common/TableRowButtons';
import { createEnumOptions } from '@/utils/core/data-transformation';
import { getEnumTranslationKey } from '@/utils/core/data-transformation/enumHelpers';
import { CoreEnums } from '@/utils/domain/constants';
import { DEFAULT_EMPTY_VALUE } from '@/utils/constants';
import { createTableActionsColumn, formatTZLocal, getEnumNamespace } from '@/utils/infrastructure';
import { createHighlightRender } from '@/utils/infrastructure/highlightColumns';
import Filters, { registerModuleFilters } from '@/utils/input/filters';

import { MODEL_NAME } from './constants';

const { BaseFilters } = Filters;

const CreatedDateFilter = {
  ...Filters.DateFilter,
  label: 'global.text.createdDate',
};

const AccessTypeFilter = {
  ...BaseFilters.EnumFilter,
  name: 'accessType',
  label: 'accessLog.text.accessType',
  enumNamespace: getEnumNamespace(CoreEnums.UserAccessType),
  enumSource: CoreEnums.UserAccessType,
  options: createEnumOptions(CoreEnums.UserAccessType, 'accessLog.accessType', {
    casing: 'camel',
  }),
};

const OperationTypeFilter = {
  ...BaseFilters.EnumFilter,
  name: 'operationType',
  label: 'accessLog.form.operationType',
  enumNamespace: getEnumNamespace(CoreEnums.UserAccessLogOperationType),
  enumSource: CoreEnums.UserAccessLogOperationType,
  options: createEnumOptions(CoreEnums.UserAccessLogOperationType, 'accessLog.operationType', {
    casing: 'camel',
  }),
};

const IsSuccessfulOperationFilter = {
  ...BaseFilters.BooleanFilter,
  name: 'isSuccessfulOperation',
  label: 'accessLog.text.isSuccessfulOperation',
  badgeOptions: {
    true: { status: 'success' },
    false: { status: 'error' },
  },
  badgeType: 'group-item',
};

const AppFilter = {
  ...BaseFilters.RemoteFilter,
  name: 'appId',
  label: 'accessLog.text.app',
  labelInValue: true,
  displayField: 'name',
  apiRoute: 'app/allowed',
  filterSearch: true,
  params: {
    select: 'id,name',
    orderBy: 'name asc',
  },
  oDataField: 'appId',
};

const PortalFilter = {
  ...BaseFilters.RemoteFilter,
  name: 'portalId',
  label: 'accessLog.text.portal',
  labelInValue: true,
  displayField: 'name',
  apiRoute: 'processportal/paged',
  filterSearch: true,
  params: {
    select: 'id,name',
    filter: 'isActive eq true',
    orderBy: 'name asc',
  },
  oDataField: 'portalId',
};

export const filtersConfig = [
  CreatedDateFilter,
  AccessTypeFilter,
  OperationTypeFilter,
  IsSuccessfulOperationFilter,
  AppFilter,
  PortalFilter,
];

registerModuleFilters(MODEL_NAME, filtersConfig);

/** Maps successful-operation switch to the same yes/no badge used in the list. */
export function mapAccessLogDetailFields(rows, { item }) {
  if (!item || !rows?.length) return rows;

  return rows.map((row) => {
    if (row.name !== 'isSuccessfulOperation') return row;

    const isSuccessful = item.isSuccessfulOperation === true;
    return {
      ...row,
      type: 'badge',
      badgeType: 'group-item',
      badgeStatus: isSuccessful ? 'success' : 'error',
      value: isSuccessful ? 'global.text.yes' : 'global.text.no',
      locale: true,
    };
  });
}

export function buildAccessLogColumns({ formatMessage, openDrawerCreateOrEdit, searchCriteria }) {
  const highlight = createHighlightRender(searchCriteria);

  return [
    {
      key: 'created',
      title: formatMessage({ id: 'global.created' }),
      dataIndex: 'created',
      sorter: true,
      render: (value) => (value ? formatTZLocal(value) : DEFAULT_EMPTY_VALUE),
    },
    {
      key: 'operationType',
      title: formatMessage({ id: 'accessLog.form.operationType' }),
      dataIndex: 'operationType',
      sorter: true,
      render: (value) => {
        const translationKey = getEnumTranslationKey(
          CoreEnums.UserAccessLogOperationType,
          value,
          'accessLog.operationType',
        );
        return (
          <Badge status="default" type="process" text={formatMessage({ id: translationKey })} />
        );
      },
    },
    {
      key: 'accessType',
      title: formatMessage({ id: 'accessLog.text.accessType' }),
      dataIndex: 'accessType',
      sorter: true,
      render: (value) => {
        if (value === undefined || value === null) {
          return <Badge status="default" type="process" text={DEFAULT_EMPTY_VALUE} />;
        }
        const translationKey = getEnumTranslationKey(
          CoreEnums.UserAccessType,
          value,
          'accessLog.accessType',
        );
        return (
          <Badge status="default" type="process" text={formatMessage({ id: translationKey })} />
        );
      },
    },
    {
      key: 'appClient/applicationName',
      title: formatMessage({ id: 'accessLog.text.appClient' }),
      dataIndex: 'appClient',
      sorter: true,
      render: (item) => highlight(item?.applicationName ?? DEFAULT_EMPTY_VALUE),
    },
    {
      key: 'email',
      title: formatMessage({ id: 'global.email' }),
      dataIndex: 'email',
      sorter: true,
      render: highlight,
    },
    {
      key: 'country/name',
      title: formatMessage({ id: 'accessLog.text.country' }),
      dataIndex: 'country',
      sorter: true,
      render: (item) => highlight(item?.name ?? DEFAULT_EMPTY_VALUE),
    },
    {
      key: 'city',
      title: formatMessage({ id: 'accessLog.text.city' }),
      dataIndex: 'city',
      sorter: true,
      render: highlight,
    },
    {
      key: 'state',
      title: formatMessage({ id: 'accessLog.text.state' }),
      dataIndex: 'state',
      sorter: true,
      render: highlight,
    },
    {
      key: 'isSuccessfulOperation',
      title: formatMessage({ id: 'accessLog.text.isSuccessfulOperation' }),
      dataIndex: 'isSuccessfulOperation',
      sorter: true,
      align: 'center',
      render: (value) => {
        const textCell = formatMessage({ id: value ? 'global.text.yes' : 'global.text.no' });
        return <Badge text={textCell} status={value ? 'success' : 'error'} type="group-item" />;
      },
    },
    {
      key: 'ip',
      title: formatMessage({ id: 'accessLog.text.ip' }),
      dataIndex: 'ip',
      sorter: true,
      render: highlight,
    },
    createTableActionsColumn({
      inlineActionCount: 1,
      render: (el) => (
        <TableRowButtons
          onDetail={() => openDrawerCreateOrEdit(el.id, true)}
          formatMessage={formatMessage}
          actionOrder={{ primary: ['detail'], secondary: [] }}
        />
      ),
    }),
  ];
}
